import React, { Children, ReactNode, useEffect, useState } from 'react';
import { TabsProps } from '@mui/material/Tabs';
import Box from '../Box/Box';
import { Tabs, TabStyled, TabsRightContentSlot } from './Tab.styled';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';

interface TabProps {
  label: string;
  defaultValue?: string | number;
}

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

interface TabPanelProps {
  children?: ReactNode;
  sx?: SxProps<Theme>;
  index: number;
  value: number;
}

export type TabsWrapperProps = {
  onTabChange?: (newValue: number) => void;
  children: ReactNode;
  tabPanelStyle?: SxProps<Theme>;
  rightContentSlot?: ReactNode;
} & TabsProps;

const TabPanel = ({ children, value, index, sx, ...other }: TabPanelProps) => {
  return value === index ? (
    <Box
      className="TabPanel-root"
      sx={{ p: 1, height: '100%', width: '100%', ...sx }}
      role="tabpanel"
      hidden={value !== index}
      id={`generic-tabpanel-${index}`}
      aria-labelledby={`generic-tab-${index}`}
      {...other}
    >
      {children}
    </Box>
  ) : null;
};

const TabsWrapper = ({
  className,
  defaultValue = 0,
  children,
  onTabChange = () => {},
  tabPanelStyle,
  rightContentSlot,
  ...otherProps
}: TabsWrapperProps) => {
  const childrenArray: Array<Exclude<ReactNode, boolean | null | undefined>> = Children.toArray(children);
  const [value, setValue] = useState<number>(0);

  useEffect(() => {
    setValue(defaultValue as number);
  }, [defaultValue, setValue]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(() => {
      onTabChange(newValue);
      return newValue;
    });
  };

  return (
    <Box className={className} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tabs
        TabIndicatorProps={{ sx: { height: '3px' } }}
        value={value}
        onChange={handleChange}
        sx={{ pb: 0.5 }}
        {...otherProps}
      >
        {childrenArray.map((child, index) => {
          if (React.isValidElement(child)) {
            const {
              props: { children, label = 'no label', testId, customProps, ...restProps }
            } = child;
            const tabButtonTestId = `tab-button-${testId ?? label.replace(/ /g, '')}`;
            return (
              <TabStyled
                key={`tab-styled-${label}-${index}`}
                data-testid={tabButtonTestId}
                label={label}
                {...restProps}
              />
            );
          } else {
            return null;
          }
        })}
        {rightContentSlot ? <TabsRightContentSlot>{rightContentSlot}</TabsRightContentSlot> : null}
      </Tabs>
      <Box className="TabPanel-container" sx={{ flex: 2, display: 'flex' }}>
        {childrenArray.map((child, index) => {
          if (React.isValidElement(child)) {
            const {
              props: { children, label = 'no label', value: childValue, testId, ...restProps }
            } = child;
            const tabContentTestId = `tab-content-${testId ?? label.replace(/ /g, '')}`;
            return (
              <TabPanel
                value={value}
                index={childValue ?? index}
                key={`tab-panel-${label}-${index}`}
                data-testid={tabContentTestId}
                sx={tabPanelStyle}
              >
                {child}
              </TabPanel>
            );
          } else {
            return null;
          }
        })}
      </Box>
    </Box>
  );
};

export default TabsWrapper;
